import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  getAllDonorsLight,
  searchDonors,
  getDonor,
  createDonor,
  updateDonor,
  deleteDonor,
  getDonorsWithOrdersByDate,
  getDonorCategories,
  createDonorCategory,
  updateDonorCategory,
  deleteDonorCategory,
  getDonorNotesCount,
  getDonorDocuments,
  createDonorDocument,
  deleteDonorDocument,
  getDonorFeedbackByDonorIds,
  getDonorInsights,
  getDonorOrders,
  updateOrderGiftAid,
  getDonorSponsorships,
  getDonorSubscriptions,
  getDonorGiftAid,
  getDonorFeedback,
  createDonorFeedback,
  updateDonorFeedback,
  deleteDonorFeedback,
  getDonorCommunications,
  createDonorCommunication,
  getDonorTasks,
  createDonorTask,
  updateDonorTask,
  deleteDonorTask,
  getDonorComments,
  createDonorComment,
  updateDonorComment,
  deleteDonorComments,
  getBranchStaff,
  updateDonorAssignedStaff,
  getTopDonations
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

const EMPTY_ARRAY = [];

// Hook to fetch all donors (lightweight - only table columns)
export function useDonors(filters) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donors', organization?.id, branchId, filters],
    queryFn: () => getAllDonorsLight(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    donors: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Server-side, paginated + searchable donor hook (backed by the search_donors
// RPC). Use this for the donors LIST and the global search — it fetches one
// page at a time and searches across ALL donors in the database, instead of
// loading every donor into the browser. `useDonors` above is kept as-is for
// the many dropdown/selection/dashboard consumers that need the full list.
export function useDonorSearch({
  search = '',
  page = 0,
  pageSize = 50,
  startDate = null,
  endDate = null,
  simpleIds = null,
  enabled = true
} = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();
  const queryClient = useQueryClient();

  const queryReady = enabled && isInitialized && !!organization?.id;
  const STALE_MS = 60000;

  const makeKey = p => [
    'donor-search',
    organization?.id,
    branchId,
    search,
    p,
    pageSize,
    startDate,
    endDate,
    simpleIds
  ];
  const makeFetch = p => () =>
    searchDonors(organization.id, branchId, {
      search,
      page: p,
      pageSize,
      startDate,
      endDate,
      simpleIds
    });

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: makeKey(page),
    queryFn: makeFetch(page),
    enabled: queryReady,
    // v5 replacement for the removed keepPreviousData option: keep showing the
    // current rows while the next page/search loads instead of blanking the
    // table behind a spinner on every page change.
    placeholderData: keepPreviousData,
    staleTime: STALE_MS
  });

  const totalCount = data?.data?.totalCount || 0;

  // Prefetch the next page in the background so clicking "Next" is instant.
  useEffect(() => {
    if (!queryReady || isFetching) return;
    if ((page + 1) * pageSize >= totalCount) return;
    queryClient.prefetchQuery({
      queryKey: makeKey(page + 1),
      queryFn: makeFetch(page + 1),
      staleTime: STALE_MS
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryReady, isFetching, page, pageSize, totalCount, search]);

  return {
    donors: data?.data?.donors || EMPTY_ARRAY,
    totalCount,
    isLoading,
    isFetching,
    error: error || data?.error,
    refetch
  };
}

// Hook to bulk-fetch notes/feedback for many donors at once.
// Keyed on the sorted donor-id list so we don't re-fetch when the array
// reference changes but the ids haven't.
export function useDonorNotesBulk(donorIds) {
  const key = Array.isArray(donorIds)
    ? [...donorIds].sort().join(',')
    : '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['donorNotesBulk', key],
    queryFn: () => getDonorFeedbackByDonorIds(donorIds),
    enabled: Array.isArray(donorIds) && donorIds.length > 0,
    staleTime: 60_000
  });

  return {
    notes: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error
  };
}

// Hook to fetch single donor by ID
export function useDonor(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donor', id],
    queryFn: () => getDonor(id),
    enabled: !!id
  });

  return {
    donor: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch donors with orders by date range
export function useDonorsWithOrdersByDate(startDate, endDate) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'donorsWithOrdersByDate',
      organization?.id,
      branchId,
      startDate,
      endDate
    ],
    queryFn: () =>
      getDonorsWithOrdersByDate(organization.id, branchId, startDate, endDate),
    enabled: isInitialized && !!organization?.id && !!startDate && !!endDate
  });

  return {
    donorCount: data?.data?.donorCount || 0,
    orders: data?.data?.orders || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor
export function useCreateDonor() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async donorData => {
      const result = await createDonor({
        ...donorData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donors'] });
      await queryClient.invalidateQueries({ queryKey: ['donorsCount'] });
      toast.success('Donor created successfully');
    },
    onError: error => {
      console.error('Create donor error:', error);
      toast.error('Failed to create donor');
    }
  });

  return {
    createDonor: mutation.mutate,
    createDonorAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update donor
export function useUpdateDonor() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonor(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async (data, variables) => {
      // Invalidate both the list and the specific donor query
      await queryClient.invalidateQueries({ queryKey: ['donors'] });
      await queryClient.invalidateQueries({
        queryKey: ['donor', variables.id]
      });
      toast.success('Donor updated successfully');
    },
    onError: error => {
      console.error('Update donor error:', error);
      toast.error('Failed to update donor');
    }
  });

  return {
    updateDonor: mutation.mutate,
    updateDonorAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete donor
export function useDeleteDonor() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonor(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donors'] });
      await queryClient.invalidateQueries({ queryKey: ['donorsCount'] });
      toast.success('Donor deleted successfully');
    },
    onError: error => {
      console.error('Delete donor error:', error);
      toast.error('Failed to delete donor');
    }
  });

  return {
    deleteDonor: mutation.mutate,
    deleteDonorAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR CATEGORIES                                 */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor categories (shared across all organizations/branches)
export function useDonorCategories() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorCategories'],
    queryFn: getDonorCategories
  });

  return {
    categories: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor category
export function useCreateDonorCategory() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async categoryData => {
      const result = await createDonorCategory({
        ...categoryData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorCategories'] });
      toast.success('Category created successfully');
    },
    onError: error => {
      console.error('Create category error:', error);
      toast.error('Failed to create category');
    }
  });

  return {
    createCategory: mutation.mutate,
    createCategoryAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update donor category
export function useUpdateDonorCategory() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonorCategory(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorCategories'] });
      toast.success('Category updated successfully');
    },
    onError: error => {
      console.error('Update category error:', error);
      toast.error('Failed to update category');
    }
  });

  return {
    updateCategory: mutation.mutate,
    updateCategoryAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete donor category
export function useDeleteDonorCategory() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonorCategory(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorCategories'] });
      toast.success('Category deleted successfully');
    },
    onError: error => {
      console.error('Delete category error:', error);
      toast.error('Failed to delete category');
    }
  });

  return {
    deleteCategory: mutation.mutate,
    deleteCategoryAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR NOTES/FEEDBACK                             */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor notes/feedback count
export function useDonorNotesCount(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorNotesCount', donorId],
    queryFn: () => getDonorNotesCount(donorId),
    enabled: !!donorId
  });

  return {
    notesCount: data?.data || 0,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR INSIGHTS                                   */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor insights/stats
export function useDonorInsights(donorId) {
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorInsights', donorId, branchId],
    queryFn: () => getDonorInsights(donorId, branchId),
    enabled: !!donorId
  });

  return {
    insights: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR ORDERS                                     */
/* -------------------------------------------------------------------------- */

// Hook to fetch orders for a specific donor
export function useDonorOrders(donorId) {
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorOrders', donorId, branchId],
    queryFn: () => getDonorOrders(donorId, branchId),
    enabled: !!donorId
  });

  return {
    orders: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to update gift aid status
export function useUpdateOrderGiftAid() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ orderId, giftAidEligible }) => {
      const result = await updateOrderGiftAid(orderId, giftAidEligible);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorOrders'] });
      toast.success('Gift Aid status updated successfully');
    },
    onError: error => {
      console.error('Update gift aid error:', error);
      toast.error('Failed to update Gift Aid status');
    }
  });

  return {
    updateGiftAid: mutation.mutate,
    updateGiftAidAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR SPONSORSHIPS                               */
/* -------------------------------------------------------------------------- */

// Hook to fetch sponsorships for a specific donor
export function useDonorSponsorships(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorSponsorships', donorId],
    queryFn: () => getDonorSponsorships(donorId),
    enabled: !!donorId
  });

  return {
    sponsorships: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR GIFT AID                                   */
/* -------------------------------------------------------------------------- */

// Hook to fetch gift aid data for a specific donor
export function useDonorGiftAid(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorGiftAid', donorId],
    queryFn: () => getDonorGiftAid(donorId),
    enabled: !!donorId
  });

  return {
    giftAidData: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR FEEDBACK/NOTES                             */
/* -------------------------------------------------------------------------- */

// Hook to fetch feedback/notes for a specific donor
export function useDonorFeedback(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorFeedback', donorId],
    queryFn: () => getDonorFeedback(donorId),
    enabled: !!donorId
  });

  return {
    feedback: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor feedback
export function useCreateDonorFeedback() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async feedbackData => {
      const result = await createDonorFeedback({
        ...feedbackData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
      await queryClient.invalidateQueries({ queryKey: ['donorNotesCount'] });
      toast.success('Note created successfully');
    },
    onError: error => {
      console.error('Create feedback error:', error);
      toast.error('Failed to create note');
    }
  });

  return {
    createFeedback: mutation.mutate,
    createFeedbackAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update donor feedback
export function useUpdateDonorFeedback() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonorFeedback(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
      toast.success('Note updated successfully');
    },
    onError: error => {
      console.error('Update feedback error:', error);
      toast.error('Failed to update note');
    }
  });

  return {
    updateFeedback: mutation.mutate,
    updateFeedbackAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete donor feedback
export function useDeleteDonorFeedback() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonorFeedback(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorFeedback'] });
      await queryClient.invalidateQueries({ queryKey: ['donorNotesCount'] });
      toast.success('Note deleted successfully');
    },
    onError: error => {
      console.error('Delete feedback error:', error);
      toast.error('Failed to delete note');
    }
  });

  return {
    deleteFeedback: mutation.mutate,
    deleteFeedbackAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                         DONOR COMMUNICATIONS                               */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor communications
export function useDonorCommunications(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorCommunications', donorId],
    queryFn: () => getDonorCommunications(donorId),
    enabled: !!donorId
  });

  return {
    communications: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor communication
export function useCreateDonorCommunication() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async communicationData => {
      const result = await createDonorCommunication({
        ...communicationData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['donorCommunications']
      });
      toast.success('Communication created successfully');
    },
    onError: error => {
      console.error('Create communication error:', error);
      toast.error('Failed to create communication');
    }
  });

  return {
    createCommunication: mutation.mutate,
    createCommunicationAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR TASKS                                      */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor tasks
export function useDonorTasks(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorTasks', donorId],
    queryFn: () => getDonorTasks(donorId),
    enabled: !!donorId
  });

  return {
    tasks: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor task
export function useCreateDonorTask() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async taskData => {
      const result = await createDonorTask({
        ...taskData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorTasks'] });
      toast.success('Task created successfully');
    },
    onError: error => {
      console.error('Create task error:', error);
      toast.error('Failed to create task');
    }
  });

  return {
    createTask: mutation.mutate,
    createTaskAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update donor task
export function useUpdateDonorTask() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonorTask(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorTasks'] });
      toast.success('Task updated successfully');
    },
    onError: error => {
      console.error('Update task error:', error);
      toast.error('Failed to update task');
    }
  });

  return {
    updateTask: mutation.mutate,
    updateTaskAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete donor task
export function useDeleteDonorTask() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonorTask(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorTasks'] });
      toast.success('Task deleted successfully');
    },
    onError: error => {
      console.error('Delete task error:', error);
      toast.error('Failed to delete task');
    }
  });

  return {
    deleteTask: mutation.mutate,
    deleteTaskAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR COMMENTS                                   */
/* -------------------------------------------------------------------------- */

// Hook to fetch donor comments
export function useDonorComments(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorComments', donorId],
    queryFn: () => getDonorComments(donorId),
    enabled: !!donorId
  });

  return {
    comments: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create donor comment
export function useCreateDonorComment() {
  const queryClient = useQueryClient();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async commentData => {
      const result = await createDonorComment({
        ...commentData,
        branch_id: branchId
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorComments'] });
      toast.success('Comment created successfully');
    },
    onError: error => {
      console.error('Create comment error:', error);
      toast.error('Failed to create comment');
    }
  });

  return {
    createComment: mutation.mutate,
    createCommentAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update donor comment
export function useUpdateDonorComment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonorComment(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorComments'] });
      toast.success('Comment updated successfully');
    },
    onError: error => {
      console.error('Update comment error:', error);
      toast.error('Failed to update comment');
    }
  });

  return {
    updateComment: mutation.mutate,
    updateCommentAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete donor comments
export function useDeleteDonorComments() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ids => {
      const result = await deleteDonorComments(ids);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorComments'] });
      toast.success('Comments deleted successfully');
    },
    onError: error => {
      console.error('Delete comments error:', error);
      toast.error('Failed to delete comments');
    }
  });

  return {
    deleteComments: mutation.mutate,
    deleteCommentsAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           STAFF                                            */
/* -------------------------------------------------------------------------- */

// Hook to fetch branch staff
export function useBranchStaff(branchId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branchStaff', branchId],
    queryFn: () => getBranchStaff(branchId),
    enabled: !!branchId
  });

  return {
    staff: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to update donor assigned staff
export function useUpdateDonorAssignedStaff() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ donorId, staffId }) => {
      const result = await updateDonorAssignedStaff(donorId, staffId);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donor'] });
      await queryClient.invalidateQueries({ queryKey: ['donors'] });
      toast.success('Assigned Staff Updated');
    },
    onError: error => {
      console.error('Update assigned staff error:', error);
      toast.error('Failed to update assigned staff');
    }
  });

  return {
    updateAssignedStaff: mutation.mutate,
    updateAssignedStaffAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

/* -------------------------------------------------------------------------- */
/*                           TOP DONATIONS                                    */
/* -------------------------------------------------------------------------- */

// Hook to fetch top donations for a donor
export function useTopDonations(donorId, limit = 3) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['topDonations', donorId, limit],
    queryFn: () => getTopDonations(donorId, limit),
    enabled: !!donorId
  });

  return {
    topDonations: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                           DONOR SUBSCRIPTIONS                              */
/* -------------------------------------------------------------------------- */

// Hook to fetch subscriptions for a specific donor
export function useDonorSubscriptions(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donor-subscriptions', donorId],
    queryFn: () => getDonorSubscriptions(donorId),
    enabled: !!donorId
  });

  return {
    subscriptions: data?.data || EMPTY_ARRAY,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

/* -------------------------------------------------------------------------- */
/*                            DONOR DOCUMENTS                                 */
/* -------------------------------------------------------------------------- */

export function useDonorDocuments(donorId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donorDocuments', donorId],
    queryFn: () => getDonorDocuments(donorId),
    enabled: !!donorId
  });

  return {
    documents: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useCreateDonorDocument() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async docData => {
      const result = await createDonorDocument({
        ...docData,
        branch_id: branchId,
        organisation_id: organization?.id
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorDocuments'] });
      toast.success('Document uploaded successfully');
    },
    onError: error => {
      console.error('Create donor document error:', error);
      toast.error('Failed to upload document');
    }
  });

  return {
    createDocument: mutation.mutate,
    createDocumentAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

export function useDeleteDonorDocument() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonorDocument(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donorDocuments'] });
      toast.success('Document deleted');
    },
    onError: error => {
      console.error('Delete donor document error:', error);
      toast.error('Failed to delete document');
    }
  });

  return {
    deleteDocument: mutation.mutate,
    deleteDocumentAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}
